import React, { useEffect, useMemo, useState } from "react";
import "react-medium-image-zoom/dist/styles.css";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  Row,
  RSelect,
} from "../../components/Component";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";

import { ErrorMessage } from "@hookform/error-message";
import * as moment from "moment";
import {
  Controller,
  FormProvider,
  useForm,
  useWatch,
} from "react-hook-form";
import { useHistory, useParams } from "react-router";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { GET, POST, PUT } from "../../helper/api";
import {
  decodeOutsourcingQr,
  getDefaultBill,
  INGREDIENT_CATEGORY_ID,
  renderLabelIngredientPriceHistoryOption
} from "../../utils/Utils";
import CreateBillForm from "./billcreatecomponent/CreateBillForm";
import BillHistory from "./BillHistory";

// Các field bị khoá ở đơn gia công
const PROCESSING_BILL_OVERRIDE = {
  deliveryProviderId: null,
  deliveryFeeByWeight: 0,
  ingredientPriceHistoryId: null,
  pack: 0,
  contractId: null,
  isExchangeInternalInventory: false,
  commissions: [],
};

// Đơn giá thuê gia công lấy từ bảng "Đơn giá gia công" theo nguyên liệu
const getOutsourcingUnitPrice = (ingredientId) => GET("processing/unit-price", {}, true)
  .then(({ data }) => data?.rows?.find((e) => e.ingredientId === ingredientId)?.amount ?? null);

// Dữ liệu từ QR phiếu cân gia công (win-app) -> giá trị mặc định của form tạo đơn thuê gia công
const parseOutsourcingQrData = (encoded) => {
  try {
    const qr = decodeOutsourcingQr(encoded);
    if (!qr) {
      return null;
    }
    const isIn = qr.type === "IN";
    const createdAt = (isIn ? qr.destinationTruckWeightAt || qr.destinationTotalWeightAt : qr.departureTotalWeightAt || qr.departureTruckWeightAt) || undefined;
    return {
      ...(qr.billId ? { id: qr.billId } : {}),
      customerId: qr.customerId || "",
      warehouseId: qr.warehouseId || null,
      deliveryProviderId: qr.deliveryProviderId || null,
      ingredientId: qr.ingredientId || "",
      type: qr.type || "IN",
      truckId: qr.truckId || "",
      note: qr.note || "",
      departureMerchandiseWeight: qr.departureMerchandiseWeight || 0,
      departureTotalWeight: qr.departureTotalWeight || 0,
      departureTruckWeight: qr.departureTruckWeight || 0,
      destinationMerchandiseWeight: qr.destinationMerchandiseWeight || 0,
      destinationTotalWeight: qr.destinationTotalWeight || 0,
      destinationTruckWeight: qr.destinationTruckWeight || 0,
      finalMerchandiseWeightCol: isIn ? "destination" : "departure",
      finalDeliveryWeightCol: isIn ? "destination" : "departure",
      createdAt: moment(createdAt).format("YYYY-MM-DD HH:mm:ss"),
      isOutsourcing: true,
    };
  } catch (e) {
    return null;
  }
};

// isProcessing: tạo/xem "Đơn nhận gia công" (đơn giá lấy theo bảng đơn giá gia công)
const BillCreate = ({ isProcessing: isProcessingRoute = false }) => {
  const history = useHistory();
  const { search } = useLocation();
  const duplicateId = new URLSearchParams(search).get("duplicateId");
  const outsourcingQrData = new URLSearchParams(search).get("outsourcingQr");
  const params = useParams();
  const id = duplicateId || params.id;

  const methodForm = useForm({
    defaultValues: {
      ...getDefaultBill(),
      isOutsourcing: false,
      outsourcingProviderId: null,
      outsourcingUnitPrice: null,
      ...(isProcessingRoute ? PROCESSING_BILL_OVERRIDE : {}),
    },
    shouldFocusError: true,
    mode: 'onChange'
  });
  const {
    reset,
    getValues,
    control,
    setValue,
    register,
    errors,
    handleSubmit,
    formState: {
      isDirty
    }
  } = methodForm;

  const [loading, setLoading] = useState(false);
  const [smOption, setSmOption] = useState(false);
  const [billData, setBillData] = useState();
  const [ingredientPriceHistoryOptions, setIngredientPriceHistoryOptions] =
    useState([]);
  const [userOptions, setUserOptions] = useState([]);

  const [haveRequestUpdate, setHaveRequestUpdate] = useState(null);
  const [showFormRequestUpdate, setShowFormRequestUpdate] = useState(false);
  const [externalContractOptions, setExternalContractOptions] = useState([]);
  const isProcessing = isProcessingRoute || !!billData?.isProcessing;

  const customerId = useWatch({
    control,
    name: "customerId",
  });

  const ingredientId = useWatch({
    control,
    name: "ingredientId",
  });

  const type = useWatch({
    control,
    name: "type",
  });

  const warehouseId = useWatch({
    control,
    name: "warehouseId",
  });

  const truckId = useWatch({
    control,
    name: "truckId",
  });

  const depTruck = useWatch({
    control,
    name: "departureTruckWeight",
  });

  const depTotal = useWatch({
    control,
    name: "departureTotalWeight",
  });

  const destTruck = useWatch({
    control,
    name: "destinationTruckWeight",
  });

  const destTotal = useWatch({
    control,
    name: "destinationTotalWeight",
  });

  const currency = useWatch({
    control,
    name: "currency",
  });

  const exchangeRate = useWatch({
    control,
    name: "exchangeRate",
  });

  const unitPriceUSD = useWatch({
    control,
    name: "unitPriceUSD",
  });

  const location = useWatch({
    control,
    name: "location",
  });

  const deliveryProviderId = useWatch({
    control,
    name: "deliveryProviderId",
  });


  const billExternalContractId = useWatch({
    control,
    name: "billExternalContractId",
  });

  const numOfContainer = useWatch({
    control,
    name: "numOfContainer",
  });

  const finalMerchandiseWeightCol = useWatch({
    control,
    name: "finalMerchandiseWeightCol",
  });

  const isOutsourcing = useWatch({
    control,
    name: "isOutsourcing",
  });

  // Quét QR phiếu cân gia công: điền sẵn thông tin + bật đơn gia công + đơn giá thuê gia công
  useEffect(() => {
    if (id || !outsourcingQrData) {
      return;
    }
    const qrValues = parseOutsourcingQrData(outsourcingQrData);
    if (!qrValues) {
      toast.error("Mã QR không hợp lệ");
      return;
    }
    // Reset với customerId rỗng rồi set lại (shouldDirty) để kích hoạt tự lấy đơn giá nguyên liệu + phí vận chuyển
    reset({
      ...getDefaultBill(),
      ...qrValues,
      customerId: "",
    });
    setTimeout(() => {
      setValue("customerId", qrValues.customerId, { shouldDirty: true });
    });
    getOutsourcingUnitPrice(qrValues.ingredientId).then((amount) => {
      setValue("outsourcingUnitPrice", amount);
    });
  }, [outsourcingQrData]);

  useEffect(() => {
    if (!isDirty || !isOutsourcing || !ingredientId) {
      return;
    }
    getOutsourcingUnitPrice(ingredientId).then((amount) => {
      setValue("outsourcingUnitPrice", amount);
    });
  }, [isOutsourcing, ingredientId]);

  useEffect(() => {
    if (billData && !billData.isProcessing) {
      const listIngredientPrice = [];
      if (billData.ingredientPriceHistoryId) {
        listIngredientPrice.push({
          label: renderLabelIngredientPriceHistoryOption(
            billData.ingredientPriceHistory
          ),
          value: billData.ingredientPriceHistoryId,
          data: billData.ingredientPriceHistory,
        });
      }

      GET(
        `ingredient-price-history`,
        {
          customerId: billData.customerId,
          ingredientId: billData.ingredientId,
          type: billData.type,
          status: 'APPROVED',
        },
        true
      ).then((resp) => {
        const {
          data: { rows },
        } = resp;
        rows.forEach((row) => {
          if (row.id !== billData.ingredientPriceHistoryId) {
            listIngredientPrice.push({
              label: renderLabelIngredientPriceHistoryOption(row),
              value: row.id,
              data: row,
            });
          }
          setIngredientPriceHistoryOptions(listIngredientPrice);
        });
      });
    }
  }, [billData]);

  useEffect(() => {
    if (currency === "VND") {
      setValue("unitPriceUSD", "");
      setValue("exchangeRate", "");
    }

    if (currency === "USD") {
      setValue(
        "unitPrice",
        unitPriceUSD && exchangeRate ? unitPriceUSD * exchangeRate : ""
      );
    }
  }, [currency, unitPriceUSD, exchangeRate]);

  useEffect(() => {
    const weight = +(depTotal || 0) - +(depTruck || 0);
    setValue("departureMerchandiseWeight", weight);
    if (finalMerchandiseWeightCol === "departure") {
      setValue("numOfContainer", weight >= 35000 ? 2 : 1)
    }
  }, [depTruck, depTotal]);

  useEffect(() => {
    const weight = +(destTotal || 0) - +(destTruck || 0)
    setValue(
      "destinationMerchandiseWeight",
      weight
    );
    if (finalMerchandiseWeightCol === "destination") {
      setValue("numOfContainer", weight >= 35000 ? 2 : 1)
    }

  }, [destTruck, destTotal]);

  useEffect(() => {
    if (id) {
      GET(`/bill/${id}`, {}, true).then((resp) => {
        let {
          data: { haveRequestUpdate, ...data },
        } = resp;
        if (duplicateId) {
          data.id = `${data.id}-${moment().valueOf()}`;
        }
        if (haveRequestUpdate) {
          setHaveRequestUpdate(haveRequestUpdate);
        }
        const allowedIngredients = [
          ...Object.values(INGREDIENT_CATEGORY_ID)
        ]
        if (!allowedIngredients.includes(data.ingredientId)) {
          toast.error("Đơn hàng không hợp lệ!");
          history.goBack();
          return;
        }
        reset({
          ...data,
          createdAt: moment(data.createdAt).format("YYYY-MM-DD HH:mm:ss"),
        });
        setBillData(data);
      });
    }
  }, [params, search]);

  useEffect(() => {
    GET("/user/list-can-confirm-request-update", {}, true).then((resp) => {
      setUserOptions(
        resp.data?.map((c) => ({
          label: c.name,
          value: c.id,
          data: c,
        }))
      );
    });

    GET("/bill-external-contract/", {
      notCompleted: true,
    }, true).then(({ data }) => {
      setExternalContractOptions([
        ...data?.rows?.map((c) => ({
          label: c.name,
          value: c.id,
          data: c,
        })),
      ]);
    });

  }, []);
  useEffect(() => {
    if (!isDirty || isProcessing) {
      return
    }

    if (
      customerId &&
      warehouseId &&
      (location === 'EXTERNAL' || deliveryProviderId)
    ) {
      GET(
        `delivery-fee`,
        {
          customerId,
          warehouseId,
          deliveryProviderId,
        },
        true
      ).then((resp) => {
        const { data } = resp;

        if (data) {
          if (location === 'EXTERNAL') {
            setValue("deliveryFeeExternal", (data.rows[0]?.amountExternal || 0) * numOfContainer);
          } else {
            setValue("deliveryFeeByWeight", data.rows[0]?.amount || 0);
            setValue("deliveryFeeExternal", 0);
          }
        }
      });
    }
  }, [customerId, warehouseId, truckId, location, numOfContainer, deliveryProviderId]);

  useEffect(() => {
    if (!isDirty) {
      return
    }
    if (location !== 'EXTERNAL' || !customerId || !warehouseId || !billExternalContractId || !ingredientId) {
      return;
    }
    const externalContract = externalContractOptions.find(e => e.value === billExternalContractId && e.data.ingredientId === ingredientId)?.data;
    if (!externalContract) {
      toast.error("Đơn hàng xuất khẩu không tồn tại hoặc đã kết thúc, không được sửa");
      return;
    }
    setValue("deliveryFeeByWeight", 0);
    setValue("exchangeRate", 0);
    setValue("unitPriceUSD", 0);

    const externalContractEstimate = externalContract?.billExternalContractEstimate
    if (externalContractEstimate) {
      setValue("deliveryFeeByWeight", externalContractEstimate?.netFee?.[warehouseId] * externalContractEstimate.exchangeRate || 0);
      setValue("deliveryFeeExternal", externalContractEstimate?.deliveryFeePerContainer?.[warehouseId] * numOfContainer);
      setValue("exchangeRate", externalContractEstimate.exchangeRate || 0);
      setValue("unitPriceUSD", externalContract.contractUnitPrice / 1000 || 0);
    }
  }, [customerId, warehouseId, location, billExternalContractId, externalContractOptions, ingredientId]);

  const billExternalContractOptionsByIngredientId = useMemo(() => {
    return externalContractOptions.filter(e => e.data.ingredientId === ingredientId);
  }, [externalContractOptions, ingredientId]);
  useEffect(() => {
    if (!isDirty) {
      return
    }
    if (isProcessing) {
      if (!ingredientId || !type) {
        return;
      }
      // Phí gia công chỉ tính trên phiếu nhập
      GET("processing/unit-price", {}, true).then(({ data }) => {
        const price = data?.rows?.find((e) => e.ingredientId === ingredientId);
        setValue("unitPrice", type === "IN" ? price?.amount || 0 : 0);
        setValue("ingredientPriceHistoryId", null);
        setValue("commissions", []);
      });
      return;
    }
    if (
      customerId &&
      ingredientId &&
      type
    ) {
      GET(
        `ingredient-price-history`,
        {
          customerId,
          ingredientId,
          type,
          status: 'APPROVED',
        },
        true
      ).then((resp) => {
        const {
          data: { rows },
        } = resp;
        if (rows.length === 0) {
          setValue("unitPrice", 0);
          setValue("ingredientPriceHistoryId", null);
          setIngredientPriceHistoryOptions([]);
          return;
        }
        const row = rows.slice(-1)[0];
        setValue("unitPrice", row.amount || 0);
        setValue("ingredientPriceHistoryId", row.id || null);
        setIngredientPriceHistoryOptions(
          rows.map(row => ({
            label: renderLabelIngredientPriceHistoryOption(row),
            value: row.id,
            data: row,
          }))
        );
        if (row.commissions) {
          setValue("commissions", row.commissions.filter(e => e.type === 'SALE').map(e => ({
            customerId: e.customerId,
            commissionFeePerWeight: e.commissionFeePerWeight,
            description: e.description,
          })));
        }
      });
    }
  }, [customerId, ingredientId, type]);

  const parseDataSubmit = (data) => {
    let {
      deliveryFeeByWeight,
      deliveryFeeExternal,
      departureMerchandiseWeight,
      departureTruckWeight,
      departureTotalWeight,
      destinationMerchandiseWeight,
      destinationTruckWeight,
      destinationTotalWeight,
      ingredientId,
      status,
      truckId,
      type,
      unitPrice,
      customerId,
      customerName,
      finalMerchandiseWeightCol,
      finalDeliveryWeightCol,
      ingredientPriceHistoryId,
      location,
      note,
      pack,
      createdAt,
      currency,
      exchangeRate,
      unitPriceUSD,
      reason,
      contractId,
      reviewerId,
      warehouseId,
      billPictures,
      isExchangeInternalInventory,
      deliveryProviderId,
      billExternalContractId,
      numOfContainer,
      commissions,

    } = data;

    const formData = {
      numOfContainer,
      departureTotalWeight,
      ...(!id || billData?.departureTotalWeight != departureTotalWeight
        ? { departureTotalWeightAt: new Date() }
        : {}),
      departureMerchandiseWeight,
      ...(!id ||
        billData?.departureMerchandiseWeight != departureMerchandiseWeight
        ? { departureMerchandiseWeightAt: new Date() }
        : {}),
      departureTruckWeight,
      ...(!id || billData?.departureTruckWeight != departureTruckWeight
        ? { departureTruckWeightAt: new Date() }
        : {}),

      destinationTotalWeight,
      ...(!id || billData?.destinationTotalWeight != destinationTotalWeight
        ? { destinationTotalWeightAt: new Date() }
        : {}),
      destinationMerchandiseWeight,
      ...(!id ||
        billData?.destinationMerchandiseWeight != destinationMerchandiseWeight
        ? { destinationMerchandiseWeightAt: new Date() }
        : {}),
      destinationTruckWeight,
      ...(!id || billData?.destinationTruckWeight != destinationTruckWeight
        ? { destinationTruckWeightAt: new Date() }
        : {}),
      deliveryFeeByWeight,
      deliveryFeeExternal,
      ingredientId,
      status,
      truckId: truckId || null,
      type,
      unitPrice,
      customerId,
      customerName,
      billPictures: billPictures?.map((c) => c.src) || [],
      finalMerchandiseWeightCol,
      finalDeliveryWeightCol,
      ingredientPriceHistoryId: ingredientPriceHistoryId || null,
      location,
      note,
      pack,
      createdAt,
      currency,
      exchangeRate,
      unitPriceUSD,
      contractId: contractId || null,
      reason,
      reviewerId,
      warehouseId,
      isExchangeInternalInventory,
      deliveryProviderId,
      billExternalContractId,
      commissions: commissions || [],
      isOutsourcing: !!data.isOutsourcing,
      outsourcingProviderId: data.isOutsourcing ? data.outsourcingProviderId : null,
      outsourcingUnitPrice: data.isOutsourcing ? data.outsourcingUnitPrice : null,
      ...(isProcessing ? { ...PROCESSING_BILL_OVERRIDE, isProcessing: true } : {}),
    };
    return formData;
  };

  const onHandleSubmit = () => {
    const { id: newId } = getValues();
    setLoading(true);
    const formData = parseDataSubmit(getValues());
    const request =
      id && !duplicateId
        ? PUT(`bill/${id}`, formData, {}, true)
        : POST("bill", { ...formData, id: newId }, {}, true);

    request
      .then((res) => {
        if (id) {
          toast.success("Cập nhật thành công");
        } else {
          toast.success("Thêm thành công");
        }
        history.goBack();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onRequestUpdate = () => {
    setLoading(true);
    const formData = parseDataSubmit(getValues());
    PUT(`/bill/request-update-bill/${id}`, formData, {}, true)
      .then((res) => {
        toast.success("Gửi yêu cầu thành công");
        history.goBack();
      })
      .catch(({ response }) => {
        toast.error(
          response?.data?.error || "Có lỗi xảy ra! Vui lòng thử lại."
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!billData && !!id) {
    return <></>;
  }

  return (
    <React.Fragment>
      <Head title={isProcessing ? "Đơn nhận gia công" : "Đơn hàng"}></Head>

      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>{isProcessing ? "Đơn nhận gia công" : "Đơn hàng"}</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand mr-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v"></Icon>
                </a>
                <div
                  className="toggle-expand-content"
                  style={{ display: smOption ? "block" : "none" }}
                >
                  <ul className="d-lg-flex g-3 pt-1 pb-1 custom-filter-header justify-content-end">
                    <li>
                      <div>
                        <Button
                          color="primary"
                          onClick={() => {
                            history.goBack();
                          }}
                        >
                          <span>Trở về</span>
                        </Button>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <div className="card card-bordered card-preview">
            <div className="card-inner">
              <div
                id="DataTables_Table_1_wrapper"
                className="dataTables_wrapper dt-bootstrap4 no-footer"
              >
                <div className="my-3">
                  <FormProvider {...methodForm}>
                    <CreateBillForm
                      id={id}
                      duplicateId={id}
                      billData={billData}
                      ingredientPriceHistoryOptions={ingredientPriceHistoryOptions}
                      externalContractOptions={billExternalContractOptionsByIngredientId}
                      setLoading={setLoading}
                      isProcessing={isProcessing}
                    />

                    {showFormRequestUpdate ? (
                      <Row>
                        <Col md="6">
                          <div className="form-group">
                            <label className="form-label" htmlFor="customer">
                              Người duyệt
                              <span className="text-danger">*</span>
                            </label>
                            <div className="form-control-wrap">
                              <Controller
                                control={control}
                                name="reviewerId"
                                rules={{ required: "Vui lòng nhập thông tin" }}
                                render={({ onChange, value, name, ref }) => (
                                  <RSelect
                                    classNamePrefix="form-control"
                                    inputRef={ref}
                                    name={name}
                                    options={userOptions}
                                    value={userOptions.find(
                                      (c) => c.value === value
                                    )}
                                    onChange={(val) => onChange(val.value)}
                                  />
                                )}
                              />
                              <ErrorMessage
                                errors={errors}
                                name="reviewerId"
                                render={({ message }) => (
                                  <span className="invalid">{message}</span>
                                )}
                              />
                            </div>
                          </div>
                        </Col>
                        <Col size="12">
                          <div className="form-group">
                            <label className="form-label" >
                              Lý do cập nhật
                              <span className="text-danger">*</span>
                            </label>
                            <div className="form-control-wrap">
                              <textarea
                                rows={3}
                                className="form-control"
                                style={{ height: "unset" }}
                                name="reason"
                                ref={register({
                                  required: "Vui lòng nhập thông tin",
                                })}
                                disabled={haveRequestUpdate}
                              />
                              <ErrorMessage
                                errors={errors}
                                name="reason"
                                render={({ message }) => (
                                  <span className="invalid">{message}</span>
                                )}
                              />
                            </div>
                            <div className="mt-2">
                              <Button
                                disabled={haveRequestUpdate}
                                color="primary"
                                type="button"
                                className={"mr-2"}
                                onClick={handleSubmit(onRequestUpdate)}
                              >
                                <span>Gửi yêu cầu cập nhật</span>
                              </Button>
                              <Button
                                color="primary"
                                type="button"
                                onClick={() => setShowFormRequestUpdate(false)}
                              >
                                <span>Ẩn yêu cầu sửa đơn hàng</span>
                              </Button>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    ) : (
                      <Row>
                        <Col size="12" className={"mt-4"}>
                          <Button
                            className={`mr-2`}
                            color="primary"
                            type="button"
                            onClick={handleSubmit(onHandleSubmit)}
                            disabled={loading}
                          >
                            <span>Lưu đơn hàng</span>
                          </Button>
                          {!duplicateId && id ? (
                            <Button
                              color="primary"
                              type="button"
                              onClick={() => setShowFormRequestUpdate(true)}
                            >
                              <span>Hiện yêu cầu sửa đơn hàng</span>
                            </Button>
                          ) : (
                            <></>
                          )}
                        </Col>
                      </Row>
                    )}
                    <BillHistory />
                  </FormProvider>
                </div>
              </div>
            </div>
          </div>
        </Block>

      </Content>
    </React.Fragment>
  );
};

export default BillCreate;
